import { Avatar, Badge, Box, Button, CircularProgress, Collapse, Dialog, IconButton, Menu, MenuItem, Tooltip, Typography, useTheme } from '@mui/material'
import { ArrowDropDown, DoNotDisturb, Info, KeyboardVoiceOutlined, Mic, Videocam, VideocamOutlined, VolumeUp, Warning } from '@mui/icons-material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { getDevices, getMicrophones, getCameras } from "agora-rtc-sdk-ng"
import { Link, useNavigate, useParams } from 'react-router-dom'
import { observer } from 'mobx-react'

import { ERPLoginPageURL, formatDate, Icons, USER_CARD_COLOR_SETS } from '../../utils/utilities'
import { useMeetingActionsContext } from '../../contexts/meetingactions.context'
import { useToggleButtonsData } from '../../hooks/useToggleButtonsData'
import { useSocketsContext } from '../../contexts/sockets.context'
import { useMeetingStore } from '../../store/meeting.store'
import MeetingJoinForm from './JoinSetup/MeetingJoinForm'
import noMeetingsImg from '../../assets/no-meetings.svg'
import { useUserStore } from '../../store/user.store'
import OnlineMeetingTimer from './OnlineMeetingTimer'
import { useSettingsStore } from '../../store/settings.store'
import { getTransformedMeetingType } from '../../utils/meeting-utilities'
import { useOnlineSessionsSocketStore } from '../../store/onlinesessionsocket.store'
import { toast } from 'react-toastify'


const JoinMeetingSetup = observer( ( { openMeetingInfoDialog } ) => {


    const [initialPermissions, setInitialPermissions] = useState( { camera: '', mic: '' } )
    const [showMeetingJoinTypeDialog, setShowMeetingJoinTypeDialog] = useState( false )
    const [changingAudioDevice, setChangingAudioDevice] = useState( false )
    const [showJoinBelowTimer, setShowJoinBelowTimer] = useState( false )
    const [loginThroughEmial, setLoginThroughEmial] = useState( false )
    const [speakersAnchorEl, setSpeakersAnchorEl] = useState( null )
    const [loadingDevices, setLoadingDevices] = useState( true )
    const [micsAnchorEl, setMicsAnchorEl] = useState( null )
    const [camsAnchorEl, setCamsAnchorEl] = useState( null )
    const [speakers, setSpeakers] = useState( [] )
    const [cameras, setCameras] = useState( [] )
    const [mics, setMics] = useState( [] )

    const { cameraButtonDetails, micButtonDetails } = useToggleButtonsData()

    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()

    const { joinChannel, turnOnCamera, turnOnMicrophone, joining } = useMeetingActionsContext()

    const { palette, border, theme, layout } = useTheme()
    const { meeting_id } = useParams()
    const navigate = useNavigate()

    const SettingsStore = useSettingsStore()
    const MeetingStore = useMeetingStore()
    const UserStore = useUserStore()

    // const { levels } = useAudioLevels( MeetingStore.getLocalAudioTrack, "volumebar" )

    const isUserHost = ( ["ADMIN", "SUBADMIN"].includes( UserStore?.getUser?.user_role || "" ) ) || Boolean( MeetingStore.getCurrentMeeting.hosts?.find( h => h.user_id === MeetingStore.getLocalUser.user_id ) )

    const samAudioElement = useRef( new Audio() )
    const setupContainerRef = useRef()

    const handleStartTimeReach = useCallback( () => {
        if ( !MeetingStore.getCurrentMeetingAccessDetails.unauth )
            setShowJoinBelowTimer( true )
        else
            MeetingStore.fetchMeeting( meeting_id )
    }, [MeetingStore, meeting_id] )

    const handleCameraChange = value => {
        MeetingStore.setMeetingState( { cameraId: value } )
        if ( MeetingStore.getMeetingState.isVideoOn ) {
            const videoTrack = MeetingStore.getLocalVideoTrack
            if ( videoTrack )
                videoTrack.setDevice( MeetingStore.getMeetingState.cameraId )
        }
        setCamsAnchorEl( null )
    }

    const handleMicChange = async value => {
        MeetingStore.setMeetingState( { micId: value } )
        if ( MeetingStore.getMeetingState.isAudioOn ) {
            const audioTrack = MeetingStore.getLocalAudioTrack
            if ( audioTrack ) {
                setChangingAudioDevice( true )
                await audioTrack.setDevice( value )
                setChangingAudioDevice( false )
            }


        }
        setMicsAnchorEl( null )
    }

    const handleSpeakerChange = async value => {
        MeetingStore.setMeetingState( { speakerId: value } )

        const audioElem = samAudioElement.current

        if ( audioElem && typeof audioElem.setSinkId !== "undefined" ) {
            try {
                await audioElem.setSinkId( value )
                // setSelectedOutputId( value ) // Save in state for later reference
            } catch ( err ) {
                console.error( "Error setting output device:", err )
            }
        } else {
            console.warn( "setSinkId not supported in this browser" )
        }
        setSpeakersAnchorEl( null )
    }



    const switchSessionToCurrentTab = async () => {
        try {
            const response = await OnlineSessionSocketStore.emit( "meeting:switch-to-current-tab", { room_id: meeting_id, user_id: MeetingStore.getLocalUser.user_id } )

            if ( response && response.success ) {
                await joinChannel()
                MeetingStore.setMeetingState( { duplicateSession: false } )
            }

            if ( response && response.message ) {
                toast( response.message )
            }
        } catch ( err ) {
            console.error( err )
        }
    }

    const getDevicesPermissions = useCallback( async ( camera = false, mic = false, skip = false ) => {
        try {
            setShowMeetingJoinTypeDialog( false )
            if ( skip )
                return
            let mics = [], cams = [], audioOuts = []
            if ( mic )
                try {
                    mics = await getMicrophones()
                } catch ( e ) { }
            if ( camera )
                try {
                    cams = await getCameras()
                } catch (e) { }
            if (  mic ) {
                try {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    audioOuts = devices.filter( d => d.kind === "audiooutput" )
                } catch ( e ) { }
            }
            setMics( mics )
            setCameras( cams )
            setSpeakers( audioOuts )
            MeetingStore.setIoDevices( {
                mics: mics,
                cameras: cams,
                speakers: audioOuts
            } )
            MeetingStore.setMeetingState( {
                speakerId: audioOuts[0]?.deviceId || null,
                micId: mics[0]?.deviceId || null,
                cameraId: cams[0]?.deviceId || null
            } )

            const permissions = {}
            const cameraStatus = await navigator.permissions.query( { name: 'camera' } )
            const micStatus = await navigator.permissions.query( { name: 'microphone' } )

            permissions.camera = cameraStatus.state === 'granted'
            permissions.mic = micStatus.state === 'granted'
            MeetingStore.setMeetingState( { isCameraDisabled: !permissions.camera, isMicDisabled: !permissions.mic } )

        } catch ( err ) {
            console.log( err.message )
        } finally {
            setLoadingDevices( false )
        }
    }, [MeetingStore] )

    useEffect( () => {

        const getPermissions = async () => {
            const permissions = {}
            if ( !( 'permissions' in navigator ) ) {
                permissions.camera = 'unsupported'
                permissions.mic = 'unsupported'
            } else {
                const cameraStatus = await navigator.permissions.query( { name: 'camera' } )
                const micStatus = await navigator.permissions.query( { name: 'microphone' } )

                cameraStatus.addEventListener( 'change', async () => {
                    if ( cameraStatus.state === 'granted' ) {
                        const cams = await getCameras()
                        setCameras( cams )
                        MeetingStore.setIoDevices( {
                            cameras: cams,
                        } )
                        MeetingStore.setMeetingState( { cameraId: cams[0]?.deviceId || null } )
                        setInitialPermissions( prev => {
                            const newPermissions = { ...prev }
                            newPermissions.camera = true
                            return newPermissions
                        } )
                    }
                } )
                micStatus.addEventListener( 'change', async () => {
                    if ( micStatus.state === 'granted' ) {
                        const mics = await getMicrophones()
                        setMics( mics )
                        const devices = await navigator.mediaDevices.enumerateDevices();
                        const audioOuts = devices.filter( d => d.kind === "audiooutput" )
                        
                        MeetingStore.setIoDevices( {
                            mics: mics,
                            speakers: audioOuts
                        } )
                        console.log("Mic Permissions Upadated!!!");
                        
                        MeetingStore.setMeetingState( {
                            speakerId: audioOuts[0]?.deviceId || null,
                            micId: mics[0]?.deviceId || null
                        } )
                        setInitialPermissions( prev => {
                            const newPermissions = { ...prev }
                            newPermissions.mic = true
                            return newPermissions
                        } )
                    }
                } )

                permissions.camera = cameraStatus.state === 'granted'
                permissions.mic = micStatus.state === 'granted'
            }
console.log(permissions);

            setInitialPermissions( permissions )

            if ( sessionStorage.getItem( 'permissionsAsked' ) !== 'true' && ['discussion'].includes( MeetingStore.getCurrentMeeting?.meeting_type ) ) {
                setShowMeetingJoinTypeDialog( ( !permissions.camera || !permissions.mic ) && MeetingStore.getCurrentMeeting.can_join )
                sessionStorage.setItem( 'permissionsAsked', 'true' )
            }
            // if ( permissions.mic && permissions.camera ) {
                getDevicesPermissions( true, true )
            // }

            MeetingStore.setMeetingState( { isCameraDisabled: !permissions.camera, isMicDisabled: !permissions.mic } )
        }
        getPermissions()
    }, [MeetingStore, getDevicesPermissions] )

    // useEffect( () => {
    //     const refetchDevices = async () => {
    //         if ( initialPermissions.mic && initialPermissions.camera ) {
    //             const devices = await getDevices()
    //             const audioOuts = devices.filter( d => d.kind === "audiooutput" )
    //             setSpeakers( audioOuts )
    //             MeetingStore.setMeetingState( { speakerId: audioOuts[0]?.deviceId || null } )
    //         }
    //     }
    //     refetchDevices()
    // }, [initialPermissions, MeetingStore] )

    useEffect( () => {

        const handleKeyDown = ( event ) => {
            const { code, altKey } = event
            if ( code === 'KeyD' && altKey ) {
                event.preventDefault()
                turnOnMicrophone()
            } else if ( code === 'KeyE' && altKey ) {
                event.preventDefault()
                turnOnCamera()
            }
        }
        const container = setupContainerRef.current
        if ( container ) {
            container?.addEventListener( 'keydown', handleKeyDown )
            return () => container?.removeEventListener( 'keydown', handleKeyDown )
        }
    }, [turnOnCamera, turnOnMicrophone] )

    useEffect( () => {

        const handleHostJoined = () => {
            if ( MeetingStore.getMeetingState.waitingToJoin )
                joinChannel()
        }
        const unsubHostJoinedEvent = OnlineSessionSocketStore.on( "meeting:host-joined", handleHostJoined )

        return () => unsubHostJoinedEvent()
    }, [joinChannel, OnlineSessionSocketStore, MeetingStore] )

    return (
        <Box padding="20px" ref={setupContainerRef} tabIndex={0} sx={{ ":focus": { outline: "none", border: "none" } }} bgcolor={palette.background.paper} flexGrow={1} gap="20px" display="grid" gridTemplateColumns={{ md: "1fr 1fr", xs: "1fr" }} alignItems="center" justifyContent="center" >

            <Dialog slotProps={{ paper: { sx: { width: "90%", maxWidth: "900px", borderRadius: "20px" } } }} open={showMeetingJoinTypeDialog} >
                <Box padding="40px" display="flex" gap="20px" flexDirection="column" alignItems="center">
                    <Box component="img" src={noMeetingsImg} width="90%" maxWidth="200px" alt="joiningmeeting" />
                    <Box>
                        <Typography gutterBottom textAlign="center" component="h3" variant='h4'>Join With Your Camera and Microphone?</Typography>
                        <Typography textAlign="center" fontSize="14px" component="h4" color='GrayText' >You can always turn off these in the {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )}.</Typography>
                    </Box>
                    <Box display="flex" justifyContent="center" flexDirection="column" gap="20px" flexWrap="wrap" alignItems="center">
                        <Button color='secondary' startIcon={<VideocamOutlined />} disableElevation variant='contained' onClick={() => getDevicesPermissions( true, true )}> Use microphone and camera</Button>
                        <Box display="flex" alignItems="center" justifyContent="center" flexWrap="wrap" gap="20px">
                            <Button color="primary" variant='outlined' startIcon={<VideocamOutlined />} onClick={() => getDevicesPermissions( true, false )}>Use Camera</Button>
                            <Button color="primary" variant='outlined' startIcon={<KeyboardVoiceOutlined />} onClick={() => getDevicesPermissions( false, true )}>Use Microphone</Button>
                            <Button variant='outlined' color="error" startIcon={<DoNotDisturb />} onClick={() => getDevicesPermissions( false, false, true )}>Continue without mic or camera</Button>
                        </Box>
                    </Box>
                </Box>
            </Dialog>

            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                <Box overflow="hidden" width="90%" maxWidth="700px" sx={{ aspectRatio: "16 / 9", border: border[1] }} borderRadius={layout.borderRadius} position="relative">

                    <Box position="absolute" sx={{ inset: "0" }}>
                        <Box width="100%" height="100%" onLoadedMetadata={e => MeetingStore.setMeetingState( { isStartingVideo: false } )} hidden={!MeetingStore.getMeetingState.isVideoOn} component="video" id={`camera-preview`} />
                    </Box>

                    {( !MeetingStore.getMeetingState.isVideoOn || MeetingStore.getMeetingState.isStartingVideo ) && <Box position="absolute" display="flex" alignItems="center" justifyContent="center" sx={{ inset: "0", width: "100%", height: "100%", background: `rgba(30,30,30,1)`, }} component="div" >
                        <Typography variant='h6' component="p" fontSize="16px" color="white" fontWeight={600}>
                            {MeetingStore.getMeetingState.isVideoOn ? MeetingStore.getMeetingState.isStartingVideo ? "Switching camera on..." : "" : "Camera is off"}
                        </Typography>
                    </Box>}

                    <Box
                        sx={{
                            background: ( MeetingStore.getMeetingState.isVideoOn && !MeetingStore.getMeetingState.isStartingVideo ) ? `linear-gradient(to top,rgba(0,0,0,0.8),rgba(0,0,0,0))` : "none",
                        }}
                        padding="20px"
                        justifyContent="center"
                        zIndex={10}
                        position="absolute"
                        bottom="0"
                        width="100%"
                        display={{ sm: "flex", xs: "none" }}
                        gap="10px"
                        alignItems="center"
                    >
                        <Tooltip title={cameraButtonDetails.tooltip} disableInteractive  >
                            <Badge color='warning' overlap='circular' invisible={cameraButtonDetails.hideBadge} badgeContent={cameraButtonDetails.badgeContent} >
                                <IconButton disabled={cameraButtonDetails.disabled} sx={{ ...cameraButtonDetails.sx, color: "white" }} onClick={() => turnOnCamera()} >
                                    {cameraButtonDetails.icon}
                                </IconButton>
                            </Badge>
                        </Tooltip>

                        <Tooltip title={micButtonDetails.tooltip} disableInteractive >
                            <Badge badgeContent={micButtonDetails.badgeContent} color='warning' overlap='circular' invisible={micButtonDetails.hideBadge} >
                                <IconButton sx={{ ...micButtonDetails.sx, color: "white" }} disabled={micButtonDetails.disabled} onClick={() => turnOnMicrophone( !MeetingStore.getMeetingState.isAudioOn )} >
                                    {micButtonDetails.icon}
                                </IconButton>
                            </Badge>
                        </Tooltip>
                        {
                            ( MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" || ( MeetingStore.getCurrentMeeting.meeting_type === "hybrid-class" && SettingsStore?.getUseSettings['hybrid_class_streaming_enabled'] ) ) &&
                            MeetingStore.getCurrentMeeting.started && !MeetingStore.getMeetingState.duplicateSession && !MeetingStore.getMeetingState.waitingToJoin &&
                            ( ( !MeetingStore.getCurrentMeetingAccessDetails.unauth && MeetingStore.getCurrentMeetingAccessDetails.hasAccess ) ||
                                ( MeetingStore.getCurrentMeetingAccessDetails.unauth && MeetingStore.getCurrentMeetingAccessDetails.hasAccess && OnlineSessionSocketStore.hasValidToken ) ||
                                ( MeetingStore.getCurrentMeetingAccessDetails.unauth && !MeetingStore.getCurrentMeetingAccessDetails.hasAccess && OnlineSessionSocketStore.hasValidToken )
                            ) &&
                            <Button startIcon={joining && <CircularProgress size={20} />} disabled={joining || !MeetingStore.getCurrentMeeting.can_join} sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, ":disabled": { color: palette.grey[500], background: palette.grey[700] } }} variant='contained' disableElevation onClick={joinChannel} >
                                {joining ? "Joining" : "Join"} {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )}
                            </Button>}
                        {MeetingStore.getLocalUser.user_id === MeetingStore.getCurrentMeeting.created_by && <IconButton sx={{ color: "white" }} onClick={openMeetingInfoDialog} >
                            <Info sx={{ color: "inherit" }} />
                        </IconButton>}
                    </Box>
                </Box>


                {/* SMALLER SCREENS */}
                <Box
                    sx={{
                        background: palette.contentBg,
                    }}
                    marginTop="20px"
                    padding="10px"
                    borderRadius={layout.borderRadius}
                    justifyContent="center"
                    zIndex={10}
                    display={{ xs: "flex", sm: "none" }}
                    gap="10px"
                    alignItems="center"
                >
                    <Tooltip title={cameraButtonDetails.tooltip} disableInteractive  >
                        <Badge color='warning' overlap='circular' invisible={cameraButtonDetails.hideBadge} badgeContent={cameraButtonDetails.badgeContent} >
                            <IconButton color="greyed" disabled={cameraButtonDetails.disabled} sx={cameraButtonDetails.sx} onClick={() => turnOnCamera()} >
                                {cameraButtonDetails.icon}
                            </IconButton>
                        </Badge>
                    </Tooltip>


                    <Tooltip title={micButtonDetails.tooltip} disableInteractive >
                        <Badge badgeContent={micButtonDetails.badgeContent} color='warning' overlap='circular' invisible={micButtonDetails.hideBadge} >
                            <IconButton color="greyed" sx={micButtonDetails.sx} disabled={micButtonDetails.disabled} onClick={() => turnOnMicrophone( !MeetingStore.getMeetingState.isAudioOn )} >
                                {micButtonDetails.icon}
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    {
                        ( MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" || ( MeetingStore.getCurrentMeeting.meeting_type === "hybrid-class" && SettingsStore?.getUseSettings['hybrid_class_streaming_enabled'] ) ) &&
                        MeetingStore.getCurrentMeeting.started && !MeetingStore.getMeetingState.duplicateSession && !MeetingStore.getMeetingState.waitingToJoin &&
                        ( ( !MeetingStore.getCurrentMeetingAccessDetails.unauth && MeetingStore.getCurrentMeetingAccessDetails.hasAccess ) ||
                            ( MeetingStore.getCurrentMeetingAccessDetails.unauth && MeetingStore.getCurrentMeetingAccessDetails.hasAccess && OnlineSessionSocketStore.hasValidToken )
                        ) && <Button disabled={joining || !MeetingStore.getCurrentMeeting.can_join} sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, ":disabled": { color: palette.grey[500], background: palette.grey[700] } }} variant='contained' startIcon={joining && <CircularProgress size={20} />} disableElevation onClick={joinChannel} >
                            {joining ? "Joining" : "Join"} {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )}
                        </Button>}
                    {MeetingStore.getLocalUser.user_id === MeetingStore.getCurrentMeeting.created_by && <IconButton sx={{ color: "white" }} onClick={openMeetingInfoDialog} >
                        <Info sx={{ color: "inherit" }} />
                    </IconButton>}
                </Box>

                {/* DEVICE SETTINGS */}
                {!loadingDevices && ( MeetingStore.getCurrentMeeting.meeting_type !== 'hybrid-class' || isUserHost ) && <Box justifyContent='center' display="flex" alignItems="flex-end" gap="10px" flexWrap="wrap" padding="20px">
                    <Button disabled={!MeetingStore.getMeetingState?.cameraId} onClick={e => setCamsAnchorEl( e.currentTarget )} endIcon={<ArrowDropDown fontSize='small' />} startIcon={<Videocam fontSize='small' />} color="greyed" variant='outlined'>
                        <Typography color={!MeetingStore.getMeetingState?.cameraId ? "GrayText" : palette.common.font} variant='subtitle2' noWrap maxWidth="80px" >{!MeetingStore.getMeetingState?.cameraId ? "Permission required..." : cameras.find( cam => cam.deviceId === MeetingStore.getMeetingState?.cameraId )?.label}</Typography>
                    </Button>
                    <Button disabled={!MeetingStore.getMeetingState?.micId || changingAudioDevice} onClick={e => setMicsAnchorEl( e.currentTarget )} endIcon={<ArrowDropDown fontSize='small' />} startIcon={<Mic fontSize='small' />} color="greyed" variant='outlined'>
                        <Typography color={!MeetingStore.getMeetingState?.micId ? "GrayText" : palette.common.font} variant='subtitle2' noWrap maxWidth="80px" >{!MeetingStore.getMeetingState?.micId ? "Permission required..." : mics.find( mic => mic.deviceId === MeetingStore.getMeetingState?.micId )?.label}</Typography>
                    </Button>
                    <Button disabled={!MeetingStore.getMeetingState?.speakerId} onClick={e => setSpeakersAnchorEl( e.currentTarget )} endIcon={<ArrowDropDown fontSize='small' />} startIcon={<VolumeUp fontSize='small' />} color="greyed" variant='outlined'>
                        <Typography color={!MeetingStore.getMeetingState?.speakerId ? "GrayText" : palette.common.font} variant='subtitle2' noWrap maxWidth="80px" >{!MeetingStore.getMeetingState?.speakerId ? "Permission required..." : speakers.find( speaker => speaker.deviceId === MeetingStore.getMeetingState?.speakerId )?.label}</Typography>
                    </Button>

                    <Menu disableAutoFocus disableEnforceFocus disableRestoreFocus onClose={() => setSpeakersAnchorEl( null )} anchorEl={speakersAnchorEl} transformOrigin={{ vertical: "bottom", horizontal: "left" }} anchorOrigin={{ vertical: "top", horizontal: "left" }} open={Boolean( speakersAnchorEl )}  >
                        {speakers.map( speaker => (
                            <MenuItem onClick={() => handleSpeakerChange( speaker.deviceId )} dense selected={speaker.deviceId === MeetingStore.getMeetingState?.speakerId} sx={{ fontSize: "14px" }} value={speaker.deviceId} key={speaker.deviceId}>{speaker.label}</MenuItem>
                        ) )}
                    </Menu>
                    <Menu disableAutoFocus disableEnforceFocus disableRestoreFocus onClose={() => setMicsAnchorEl( null )} anchorEl={micsAnchorEl} transformOrigin={{ vertical: "bottom", horizontal: "left" }} anchorOrigin={{ vertical: "top", horizontal: "left" }} open={Boolean( micsAnchorEl )}  >
                        {mics.map( mic => (
                            <MenuItem onClick={() => handleMicChange( mic.deviceId )} dense selected={mic.deviceId === MeetingStore.getMeetingState?.micId} sx={{ fontSize: "14px" }} value={mic.deviceId} key={mic.deviceId}>{mic.label}</MenuItem>
                        ) )}
                    </Menu>
                    <Menu disableAutoFocus disableEnforceFocus disableRestoreFocus onClose={() => setCamsAnchorEl( null )} anchorEl={camsAnchorEl} transformOrigin={{ vertical: "bottom", horizontal: "left" }} anchorOrigin={{ vertical: "top", horizontal: "left" }} open={Boolean( camsAnchorEl )}  >
                        {cameras.map( cam => (
                            <MenuItem onClick={() => handleCameraChange( cam.deviceId )} dense selected={cam.deviceId === MeetingStore.getMeetingState?.cameraId} sx={{ fontSize: "14px" }} value={cam.deviceId} key={cam.deviceId}>{cam.label}</MenuItem>
                        ) )}
                    </Menu>


                </Box>}
            </Box>

            {/* MEETING DETAILS AND PERMISSIONS */}
            <Box display="flex" flexGrow={1} overflow="hidden" flexDirection="column" alignItems="center" >
                <Typography textAlign="center" variant='h4' maxWidth="100%" fontWeight="600">{MeetingStore.getCurrentMeeting.title}  </Typography>
                <Typography textAlign="center" fontSize="12px" maxWidth="100%" marginTop="-5px" variant="h6">{MeetingStore.getCurrentMeeting.creator_data?.name}</Typography>
                <Typography textAlign="center" variant='h6' maxWidth="100%" fontWeight="600">{formatDate( MeetingStore.getCurrentMeeting.starts_at, { format: "DD-MM-YYYY" } )}, {formatDate( MeetingStore.getCurrentMeeting.starts_at, { format: "hh:mm A" } )} - {formatDate( MeetingStore.getCurrentMeeting.ends_at, { format: "hh:mm A" } )}  </Typography>

                {!MeetingStore.getCurrentMeeting.can_join && ( MeetingStore.getCurrentMeeting.meeting_type !== "hybrid-class" || ( MeetingStore.getCurrentMeeting.meeting_type === "hybrid-class" && SettingsStore?.getUseSettings['hybrid_class_streaming_enabled'] ) ) && <Box>
                    {!MeetingStore.getCurrentMeeting.started && !showJoinBelowTimer && <Box maxWidth="350px" marginTop="20px" display="flex" flexDirection="column" alignItems="stretch" borderRadius="10px" border={`1px solid ${palette.primary.dark}`}>
                        {!showJoinBelowTimer && <Typography alignItems="center" gap="10px" display="flex" textTransform="capitalize" color="primary" variant="h6" padding="5px 20px" borderBottom={`1px solid ${palette.primary.dark}`} bgcolor={palette.primary.main + "22"} > <Info sx={{ fontSize: "20px" }} /> {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} yet to start! </Typography>}
                        {!showJoinBelowTimer && <Typography fontSize="12px" padding="5px 20px" textAlign="center">You can join the {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type )} in</Typography>}
                        {!showJoinBelowTimer && <OnlineMeetingTimer onTriggerIfLessThan={MeetingStore.getCurrentMeeting.created_by === MeetingStore.getLocalUser.user_id ? handleStartTimeReach : null} triggerIfLessThan={MeetingStore.getCurrentMeeting.created_by === MeetingStore.getLocalUser.user_id ? ( ( !Number.isNaN( parseInt( SettingsStore.getUseSettings['meeting_host_grace_start_time'] ) ) ? parseInt( SettingsStore.getUseSettings['meeting_host_grace_start_time'] ) : 5 ) * 60 ) : null} onEnd={handleStartTimeReach} styles={{ textAlign: "center" }} fontSize="24px" />}
                        {!showJoinBelowTimer && MeetingStore.getCurrentMeeting.created_by === MeetingStore.getLocalUser.user_id && <Typography fontSize="12px" variant='subtitle2' component="span" textTransform="capitalize" color='secondary' textAlign="center" >{getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} host can join {SettingsStore.getUseSettings['meeting_host_grace_start_time'] || 5} minutes earlier!</Typography>}
                    </Box>}
                    {showJoinBelowTimer &&
                        <Button startIcon={joining && <CircularProgress size={20} />} disabled={joining} sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, marginTop: "20px", ":disabled": { color: palette.grey[500], background: palette.grey[700] } }} variant='contained' disableElevation onClick={joinChannel} >
                            {joining ? "Joining" : "Join"} {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )}
                        </Button>}
                    {MeetingStore.getCurrentMeeting.ended && <Box maxWidth="350px" marginTop="20px" display="flex" flexDirection="column" alignItems="stretch" borderRadius="10px" border={`1px solid ${palette.error.dark}`}>
                        <Typography alignItems="center" gap="10px" display="flex" color="error" variant="h6" padding="5px 20px" textTransform="capitalize" borderBottom={`1px solid ${palette.error.dark}`} bgcolor={palette.error.main + "22"} > <Info sx={{ fontSize: "20px" }} /> {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} Ended </Typography>
                        <Typography fontSize="12px" color='textSecondary' marginTop="10px" padding="0 20px" textTransform="capitalize" textAlign="center">{getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} ended at</Typography>
                        <Typography fontSize="24px" padding="0 20px 10px 20px" textAlign="center">
                            {formatDate( MeetingStore.getCurrentMeeting.ends_at, { format: "LLL" } )}
                        </Typography>
                    </Box>}
                </Box>}

                {!OnlineSessionSocketStore.hasValidToken && MeetingStore.getCurrentMeeting.can_join && !MeetingStore.getMeetingState.waitingToJoin && !MeetingStore.getMeetingState.duplicateSession && MeetingStore.getCurrentMeetingAccessDetails.unauth && MeetingStore.getCurrentMeetingAccessDetails.needsAliveLogin && <Box maxWidth="450px" marginTop="20px" borderRadius="10px" border={`1px solid ${palette.error.dark}`}>
                    <Typography alignItems="center" gap="10px" display="flex" color="error" variant="h6" padding="5px 20px" borderBottom={`1px solid ${palette.error.dark}`} bgcolor={palette.error.main + "22"} > <Warning sx={{ fontSize: "20px" }} /> Restrcited Access </Typography>
                    <Typography fontSize="12px" padding="5px 20px">This {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} is restricted to specific student groups and invited participants.
                        If you have access to ALive LMS, please log in through ALive.
                        If you received an email invitation, join using the same email ID.</Typography>


                    <Box display="flex" gap="20px" justifyContent="flex-end" padding="20px">
                        <Link to={ERPLoginPageURL} className='default-link' ><Button sx={{ borderRadius: "20px" }} variant='outlined' endIcon={Icons.default.Login} color="secondary">Sign-in to Alive</Button></Link>
                        <Button sx={{ borderRadius: "20px" }} variant='outlined' endIcon={!loginThroughEmial ? Icons.default.KeyboardArrowDownIcon : Icons.default.KeyboardArrowUp} onClick={() => setLoginThroughEmial( prev => !prev )} color="secondary">Join with E-mail</Button>
                    </Box>
                    <Collapse in={loginThroughEmial} >
                        <Box padding="20px">
                            <MeetingJoinForm fields={["email", "passkey"]} joining={joining} joinChannel={joinChannel} />
                        </Box>
                    </Collapse>
                </Box>}

                {!MeetingStore.getCurrentMeetingAccessDetails.unauth && ( MeetingStore.getCurrentMeeting.meeting_type === "hybrid-class" && !SettingsStore?.getUseSettings['hybrid_class_streaming_enabled'] ) && <Box>
                    <Box maxWidth="450px" marginTop="20px" borderRadius="10px" border={`1px solid ${palette.error.dark}`}>
                        <Typography alignItems="center" gap="10px" display="flex" color="error" variant="h6" padding="5px 20px" borderBottom={`1px solid ${palette.error.dark}`} bgcolor={palette.error.main + "22"} > <Warning sx={{ fontSize: "20px" }} /> Class disabled </Typography>
                        <Typography fontSize="14px" marginTop="10px" padding="10px 20px">
                            This {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} has been disabled for your institue. Please contact Alive team for further details.
                        </Typography>
                        <Box display="flex" gap="10px" justifyContent="flex-end" padding="20px">
                            <Button onClick={() => navigate( -1 )} sx={{ borderRadius: "20px" }} startIcon={Icons.default.ArrowBack} color="secondary">Go back</Button>
                            <Button onClick={() => navigate( `/${UserStore.getUser.user_role === "STUDENT" ? "student" : ["HOD", "FACULTY", "PRINCIPAL"].includes( UserStore.getUser.user_role ) ? "faculty" : ""}/hybrid-class${["HOD", "FACULTY", "PRINCIPAL", "STUDENT"].includes( UserStore.getUser.user_role ) ? "" : "es"}/${meeting_id}` )} sx={{ borderRadius: "20px" }} variant='contained' endIcon={Icons.default.HybridClasses} color="primary" disableElevation>View Hybrid Class</Button>
                        </Box>
                    </Box>
                </Box>}

                {MeetingStore.getCurrentMeeting.can_join && !MeetingStore.getMeetingState.duplicateSession && !MeetingStore.getCurrentMeetingAccessDetails.unauth && !MeetingStore.getCurrentMeetingAccessDetails.needsAliveLogin && !MeetingStore.getCurrentMeetingAccessDetails.hasAccess && <Box maxWidth="350px" marginTop="20px" borderRadius="10px" border={`1px solid ${palette.error.dark}`}>
                    <Typography alignItems="center" gap="10px" display="flex" color="error" variant="h6" padding="5px 20px" borderBottom={`1px solid ${palette.error.dark}`} bgcolor={palette.error.main + "22"} > <Warning sx={{ fontSize: "20px" }} /> No Access </Typography>
                    <Box bgcolor={palette.contentBg} padding="5px 20px">
                        <Typography color='success' variant='subtitle2'>You are signed in as {UserStore.getUser.name}</Typography>

                        <Typography width="fit-content" fontSize="10px" sx={{ ":hover": { textDecoration: "underline" } }} color='secondary'> <Link className='default-link' to={ERPLoginPageURL}>Switch Account</Link></Typography>

                    </Box>
                    <Typography fontSize="12px" padding="5px 20px">You do not have access to this {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )}! please contact {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} host for access. </Typography>
                    <Box display="flex" justifyContent="flex-end" padding="5px 20px">
                        <Button onClick={() => navigate( `${["HOD", "PRINCIPAL", "FACULTY"].includes( UserStore.getUser.user_role ) ? "/faculty" : ""}/meetings` )} color="secondary">Back to Meetings</Button>
                    </Box>
                </Box>}
                {!OnlineSessionSocketStore.hasValidToken && MeetingStore.getCurrentMeeting.can_join && !MeetingStore.getMeetingState.duplicateSession && MeetingStore.getCurrentMeetingAccessDetails.unauth && MeetingStore.getCurrentMeetingAccessDetails.hasAccess && <Box maxWidth="450px" marginTop="20px" display="flex" alignItems="center" flexDirection="column" gap="20px" borderRadius="10px">
                    <MeetingJoinForm joining={joining} joinChannel={joinChannel} />
                </Box>}

                {MeetingStore.getCurrentMeeting.can_join && MeetingStore.getMeetingState.duplicateSession && <Box maxWidth="350px" marginTop="40px" display="flex" flexDirection="column" alignItems="center" borderRadius="10px">
                    <Avatar sx={{ background: USER_CARD_COLOR_SETS[2].primary, color: "white" }} >{MeetingStore.getLocalUser.name?.[0]}</Avatar>
                    <Typography alignItems="center" gap="10px" textAlign="center" display="flex" variant="h6" component="h3" padding="5px 20px"  >  Seems like you are already in the {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} on another tab!</Typography>
                    <Box display="flex" justifyContent="center" padding="5px 20px">
                        <Button variant='contained' disabled={joining} startIcon={joining && <CircularProgress size={20} />} disableElevation onClick={switchSessionToCurrentTab} color="secondary">Join Here</Button>
                    </Box>
                </Box>}

                {MeetingStore.getMeetingState.waitingToJoin && MeetingStore.getCurrentMeeting.can_join && !MeetingStore.getMeetingState.duplicateSession && MeetingStore.getCurrentMeetingAccessDetails.hasAccess && <Box marginTop="20px" justifyContent="center" display="flex" flexWrap="wrap" alignItems="center" gap="10px">
                    <CircularProgress color='secondary' size={20} />
                    <Typography textAlign="center" color={theme === 'dark' ? `rgba(200,200,200,1)` : `rgba(48,48,48,1)`} fontSize="24px">Waiting for {getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type, { forDisplay: true } )} host to let you in...</Typography>
                </Box>}



            </Box>
        </Box >

    )
}
)
export default JoinMeetingSetup